import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listScripts, getScript, saveScript, resetScript, getVoices, getConfig, saveConfig, saveCourseMeta, retagAll,
  build, getJob, listOutputs, makeSample, makeSamplePage, makeCoverPage, getStore, saveStore, publishStore, unpublishStore, uploadStoreCover,
  type ScriptMeta, type VoiceRole, type VoiceConfig, type Job, type Output, type ScriptDetail, type JobStats, type FonVoice, type CourseMeta, type StoreCopy,
} from '../lib/audio-course-client';
import { STORE_BLOCKS, STORE_SEEDS, emptyStore, assembleStoreMarkdown, serializeStorePayload } from '../lib/store-copy-defaults';
import ImagePicker, { type ImagePickerSlot } from '../components/ImagePicker';
import LdpStorePanel from './LdpStorePanel';
import { editionOf, languagesOf, coursesOf, languageOfCourse } from '../components/CoursePicker';
import CourseSettings, { type CatalogEntry } from '../components/CourseSettings';

// PUBLIC course catalog — the single source of what the page offers. Mirrors the buildable
// courses in the backend COURSES registry, but PRIVATE editions (michael / personal / family)
// are deliberately omitted so they are never listed, displayed, or wired here. `code` is the
// backend course-id (drives /scripts, /outputs, /build); `target` groups locale variants under
// one language; `country` is the market it serves (matches course-countries.ts); `course` is
// the edition label. To add a course, add a row — nothing else is hardcoded.
const CATALOG: CatalogEntry[] = [
  { code: 'EUS_TRAVEL',  target: 'Basque',              country: 'ES', course: 'Travel' },
  { code: 'BUL_TRAVEL',  target: 'Bulgarian',           country: 'BG', course: 'Travel' },
  { code: 'ARZ',        target: 'Egyptian Arabic',      country: 'EG', course: 'Travel' },
  { code: 'FRA',        target: 'French',               country: 'FR', course: 'Travel' },
  { code: 'KAT_TRAVEL', target: 'Georgian',             country: 'GE', course: 'Travel' },
  { code: 'DEU',        target: 'German',               country: 'DE', course: 'Travel' },
  { code: 'ELL',        target: 'Greek',                country: 'GR', course: 'Travel' },
  { code: 'HEB',        target: 'Hebrew',               country: 'IL', course: 'Travel' },
  { code: 'HUN',        target: 'Hungarian',            country: 'HU', course: 'Travel' },
  { code: 'IND_TRAVEL',  target: 'Indonesian',          country: 'ID', course: 'Travel' },
  { code: 'GLE',        target: 'Irish',                country: 'IE', course: 'Travel' },
  { code: 'ITA',        target: 'Italian',              country: 'IT', course: 'Travel' },
  { code: 'JPN_TRAVEL', target: 'Japanese',             country: 'JP', course: 'Travel' },
  { code: 'KAZ',        target: 'Kazakh',               country: 'KZ', course: 'Travel' },
  { code: 'KOR',        target: 'Korean',               country: 'KR', course: 'Travel' },
  { code: 'KIR',        target: 'Kyrgyz',               country: 'KG', course: 'Travel' },
  { code: 'CMN_TW_TRAVEL', target: 'Mandarin (Taiwan)', country: 'TW', course: 'Travel' },
  { code: 'KHK_TRAVEL', target: 'Mongolian (Khalkha)',  country: 'MN', course: 'Travel' },
  { code: 'NPI_SAR',    target: 'Nepali',               country: 'NP', course: 'Search & Rescue' },
  { code: 'PES',        target: 'Persian',              country: 'IR', course: 'Travel' },
  { code: 'POR_BR',     target: 'Portuguese',           country: 'BR', course: 'Travel' },
  { code: 'POR_PT',     target: 'Portuguese',           country: 'PT', course: 'Travel' },
  { code: 'RON',        target: 'Romanian',             country: 'RO', course: 'Travel' },
  { code: 'RUS',        target: 'Russian',              country: 'RU', course: 'Travel' },
  { code: 'SPA_MX_TRAVEL', target: 'Spanish',           country: 'MX', course: 'Travel' },
  { code: 'SPA_ES',     target: 'Spanish',              country: 'ES', course: 'Travel' },
  { code: 'SPA_US_NURSING_GENERAL', target: 'Spanish',  country: 'US', course: 'Nursing (General Ward)' },
  { code: 'SWH',        target: 'Swahili',              country: 'TZ', course: 'Travel' },
  { code: 'THA_TRAVEL',  target: 'Thai',                country: 'TH', course: 'Travel' },
  { code: 'TUR_TRAVEL',  target: 'Turkish',             country: 'TR', course: 'Travel' },
  { code: 'UZN',        target: 'Uzbek',                country: 'UZ', course: 'Travel' },
  { code: 'VIE_TRAVEL',  target: 'Vietnamese',          country: 'VN', course: 'Travel' },
  { code: 'ZUL',        target: 'Zulu',                 country: 'ZA', course: 'Travel' },
];
// Flat {code,name} list for the Store-copy tab + its helpers (public only).
const LANGS = CATALOG.map((c) => ({ code: c.code, name: `${c.target} — ${c.course}` }));
// Course-id → display label, for headers (Library, etc.).
const COURSE_LABEL: Record<string, string> = Object.fromEntries(LANGS.map((l) => [l.code, l.name]));
const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const SPEEDS = [1.0, 1.25, 1.5, 1.75, 2.0];
const BUILD_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];   // travel = A1–B2, pro courses (e.g. nursing) = B1–C2; offer all so advanced levels are selectable

// Fallback labels only; the live per-language roles + labels come from /voices
// (each course exposes its own cognate donor languages).
const ROLE_LABELS: Record<string, string> = {
  coach: 'Coach — English (all instruction & glosses)',
  model_q: 'Ask voice — target language (questions)',
  model_a: 'Answer voice — target language (answers)',
};

const fmtDur = (ms?: number) => !ms ? '—' : `${Math.floor(ms / 60000)}:${String(Math.round((ms % 60000) / 1000)).padStart(2, '0')}`;
const fmtBytes = (b?: number) => !b ? '—' : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`;

type Status = 'none' | 'current' | 'stale';
type Tab = 'build' | 'library' | 'store';

// Course-id -> store/product region code, only where they differ (we key by locale, so this is
// empty for CMN_TW/SPA_*/POR_* — the code IS the region).
const STORE_REGION: Record<string, string> = {};
// Course-id -> the book/companion language code used for sample PAGES, only where they differ.
const BOOK_LANG: Record<string, string> = { NPI_SAR: 'NPI' };
const bookLangOf = (course: string) => BOOK_LANG[course] || STORE_REGION[course] || course;
// "…p12_d1…" -> "Pattern 12 · D1"
const patLabel = (url: string) => { const m = url.match(/p(\d+)_d(\d+)/i); return m ? `Pattern ${+m[1]!} · D${+m[2]!}` : 'audio'; };

// Built audio can live under the bare course-id OR its <CODE>_TRAVEL alias — the 2026-10-21 folder
// migration moved several courses' audio to audio/course/<LANG>_TRAVEL/. Query both and merge so an
// existing course always shows its built files regardless of which code the build wrote them under.
async function outputsFor(code: string, level: string): Promise<Output[]> {
  const codes = Array.from(new Set([code, `${code}_TRAVEL`, code.replace(/_TRAVEL$/, '')]));
  const lists = await Promise.all(codes.map((c) => listOutputs(c, level).then((r) => r.outputs || []).catch(() => [] as Output[])));
  const seen = new Set<string>(); const out: Output[] = [];
  for (const l of lists) for (const o of l) if (!seen.has(o.file)) { seen.add(o.file); out.push(o); }
  return out;
}

export default function AudioCourse() {
  const [tab, setTab] = useState<Tab>('build');
  const [storeMode, setStoreMode] = useState<'ldp' | 'classic'>('ldp');
  const [lang, setLang] = useState('');       // committed course-id that drives the loaded view ('' = nothing loaded yet)
  const [level, setLevel] = useState('A1');   // committed level
  // Build-tab selection is owned by the <CourseSettings> cascade; it resolves source → target →
  // country → course → the backend course-id, and commits via loadCourse (below).
  const [scripts, setScripts] = useState<ScriptMeta[]>([]);
  const [voiceRoles, setVoiceRoles] = useState<VoiceRole[]>([]);
  const [voiceConfig, setVoiceConfig] = useState<VoiceConfig>({});
  const [voiceLang, setVoiceLang] = useState<VoiceConfig>({});
  const [usdPerAudioMin, setUsdPerAudioMin] = useState<number>(0.0496);
  const [courseMeta, setCourseMeta] = useState<CourseMeta>({ courseName: '', artist: 'Glossika', genre: 'Language Course' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [job, setJob] = useState<Job | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [outputs, setOutputs] = useState<Record<string, Output>>({});
  const [outputsPD, setOutputsPD] = useState<Record<string, Output>>({});   // same outputs keyed by pattern:day — naming-agnostic fallback (built audio may live under a twin code whose file stems differ, e.g. kor_travel_* vs kor_*)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ScriptDetail | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Library tab state
  const [libLang, setLibLang] = useState('');   // committed course-id ('' = nothing shown yet)
  const [libLevel, setLibLevel] = useState('A1');
  const [libOutputs, setLibOutputs] = useState<Output[]>([]);
  const [libLoading, setLibLoading] = useState(false);
  const [speed, setSpeed] = useState(1.0);

  const loadScripts = useCallback(async () => {
    const r = await listScripts(lang, level);
    setScripts(r.scripts);
  }, [lang, level]);
  const loadOutputs = useCallback(async () => {
    try {
      const outs = await outputsFor(lang, level);
      const map: Record<string, Output> = {};
      const pd: Record<string, Output> = {};
      for (const o of outs) { map[o.file] = o; pd[`${o.pattern}:${o.day}`] = o; }
      setOutputs(map);
      setOutputsPD(pd);
    } catch { /* ignore */ }
  }, [lang, level]);
  const loadLibrary = useCallback(async (lang: string, level: string) => {
    setLibLoading(true);
    try { setLibOutputs(await outputsFor(lang, level)); }
    catch { setLibOutputs([]); }
    finally { setLibLoading(false); }
  }, []);

  useEffect(() => {
    if (!lang) return;   // nothing committed yet — don't fetch, and don't show a default course's voices (the old KHK/Mongolian default)
    (async () => {
      setLoading(true);
      setError(null);   // clear any stale error (e.g. a transient offline load) so it doesn't persist after a successful reload
      try {
        const [, vr, cfg] = await Promise.all([loadScripts(), getVoices(lang), getConfig(lang)]);
        setVoiceRoles(vr.roles);
        let vcfg = cfg.config?.voiceConfig || {};
        let vlang = cfg.config?.voiceLang || {};
        // Voices-split recovery: if a <CODE>_TRAVEL course has no saved voices but its bare
        // sibling does (seeds were migrated to _TRAVEL while the voiceConfig stayed on bare),
        // pre-fill from the sibling so the operator just clicks "Save voices" to land them on
        // this code — no re-picking. (Uses the admin API; the Save writes CONFIG#<_TRAVEL>.)
        if (Object.keys(vcfg).length === 0 && /_TRAVEL$/i.test(lang)) {
          try {
            const bare = lang.replace(/_TRAVEL$/i, '');
            const bcfg = await getConfig(bare);
            if (bcfg.config?.voiceConfig && Object.keys(bcfg.config.voiceConfig).length) {
              vcfg = bcfg.config.voiceConfig;
              vlang = bcfg.config.voiceLang || {};
              setNotice(`Voices carried over from ${bare} (you set these earlier). Click "Save voices" to confirm them on ${lang}.`);
            }
          } catch { /* no sibling config — operator picks voices normally */ }
        }
        setVoiceConfig(vcfg);
        if (cfg.config?.meta) setCourseMeta(cfg.config.meta);
        setVoiceLang(vlang);
        setUsdPerAudioMin(cfg.config?.usdPerAudioMin ?? 0.0496);
        await loadOutputs();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, [lang, loadScripts, loadOutputs]);

  // Load library outputs when the tab opens or the filter changes.
  useEffect(() => { if (tab === 'library' && libLang) loadLibrary(libLang, libLevel); }, [tab, libLang, libLevel, loadLibrary]);

  // Poll the running job.
  useEffect(() => {
    if (!jobId) return;
    const tick = async () => {
      try {
        const r = await getJob(jobId);
        setJob(r.job);
        if (r.job.status === 'done' || r.job.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          loadOutputs();
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId, loadOutputs]);

  // Match a script to its built MP3: by exact file stem, falling back to pattern:day so a
  // course whose audio sits under a twin code (different file stems) still shows as built.
  const outFor = (s: ScriptMeta): Output | undefined =>
    outputs[s.file.replace(/\.json$/, '')] ?? outputsPD[`${s.pattern}:${s.day}`];
  const statusOf = (s: ScriptMeta): Status => {
    const o = outFor(s);
    if (!o) return 'none';
    return o.sourceVersion === s.version ? 'current' : 'stale';
  };

  const pickVoice = (role: string, v: FonVoice) => {
    setVoiceConfig((c) => ({ ...c, [role]: v.id }));
    setVoiceLang((c) => ({ ...c, [role]: v.languageCode }));
  };
  const setVoiceManual = (role: string, id: string, defaultLang: string) => {
    setVoiceConfig((c) => ({ ...c, [role]: id }));
    setVoiceLang((c) => ({ ...c, [role]: defaultLang }));
  };

  const onSaveVoices = async () => {
    setSaving(true); setError(null); setNotice(null);
    try { await saveConfig(lang, voiceConfig, usdPerAudioMin, voiceLang); setNotice('Voice selections saved.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  };

  const onSaveMeta = async () => {
    setSavingMeta(true); setError(null); setNotice(null);
    try { await saveCourseMeta(lang, courseMeta); setNotice('Course info saved. It applies to the next build — use "Re-tag existing MP3s" to apply it to already-built files.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSavingMeta(false); }
  };
  const onRetag = async () => {
    setRetagging(true); setError(null); setNotice(null);
    try { await saveCourseMeta(lang, courseMeta); await retagAll(); setNotice('Re-tagging all built MP3s with the current info (no audio rebuild, no cost). Give it a minute, then the CDN refresh runs on the next deploy or via the flush action.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Re-tag failed'); }
    finally { setRetagging(false); }
  };

  const startBuild = async (files: string[]) => {
    setError(null); setNotice(null);
    if (!files.length) { setError('Nothing to build.'); return; }
    if (!voiceConfig.coach || !voiceConfig.model_q || !voiceConfig.model_a) {
      setError('Pick at least the Coach, Ask and Answer voices, then Save, before building.'); return;
    }
    try {
      await saveConfig(lang, voiceConfig, usdPerAudioMin, voiceLang);
      const r = await build(lang, level, files, voiceConfig, usdPerAudioMin, voiceLang);
      setJobId(r.jobId);
      setJob({ id: r.jobId, status: 'queued', lang, level, files, progress: { done: 0, total: files.length } });
      setSelected(new Set());   // auto-unselect once a build starts, ready for the next batch
      setNotice(`Build started for ${files.length} file(s). Fon is rendering — cold builds take a few minutes; cached lines are instant.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Build failed to start'); }
  };

  const toggle = (file: string) => setSelected((s) => { const n = new Set(s); n.has(file) ? n.delete(file) : n.add(file); return n; });
  const openEditor = async (file: string) => {
    setError(null);
    try { setEditing(await getScript(lang, file)); } catch (e) { setError(e instanceof Error ? e.message : 'Could not open editor'); }
  };
  const afterEdit = async () => { setEditing(null); await loadScripts(); };

  // Commit a resolved course-id + level into the Build view (loads scripts/voices/config/outputs).
  const loadCourse = (code: string, lvl: string) => { setLang(code); setLevel(lvl); };

  const running = job && (job.status === 'queued' || job.status === 'running');
  const notBuilt = scripts.filter((s) => statusOf(s) !== 'current').map((s) => s.file);

  const tabBtn = (t: Tab, label: string) => (
    <button onClick={() => setTab(t)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? 'border-accent-teal text-accent-teal' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-6 max-w-6xl">
      <header>
        <h1 className="font-heading text-2xl font-bold text-gray-100">Audio Course</h1>
        <p className="text-sm text-gray-500 mt-1">Bilingual buildup drills. Build MP3s in <b>Build</b>; play &amp; download in <b>Library</b>.</p>
      </header>

      <div className="flex gap-1 border-b border-zinc-800">
        {tabBtn('build', 'Build')}
        {tabBtn('library', 'Library')}
        {tabBtn('store', 'Store copy')}
      </div>

      {error && <div className="rounded border border-red-800/50 bg-red-900/20 text-red-300 px-4 py-3 text-sm whitespace-pre-wrap">{error}</div>}
      {notice && <div className="rounded border border-teal-800/50 bg-teal-900/20 text-teal-300 px-4 py-3 text-sm">{notice}</div>}

      {tab === 'build' && (
        <div className="space-y-8">
          {/* 0 · Select course — Source → Target → Country → Course → Level → Submit. Nothing loads until Submit. */}
          <CourseSettings catalog={CATALOG} levels={BUILD_LEVELS}
            current={lang ? { code: lang, level } : undefined}
            submitLabel="Load course"
            onSubmit={(code, lvl) => loadCourse(code, lvl)} />
          {lang && <p className="text-xs text-gray-500">Loaded <b className="text-gray-300">{COURSE_LABEL[lang] || lang}</b> · {level} · {scripts.length} scripts</p>}

          {!lang && <p className="text-sm text-gray-500">Pick a <b>language</b>, then its <b>course</b>, then a <b>level</b>, and click <b>Load course</b>. Everything loads once — no waiting between choices.</p>}
          {lang && loading && <p className="text-sm text-zinc-500">Loading {COURSE_LABEL[lang] || lang} {level}…</p>}
          {lang && !loading && (<>

          {/* 1 · Voices */}
          <section className="space-y-4">
            <h2 className="font-heading text-lg font-semibold text-gray-200">1 · Voices</h2>
            <p className="text-xs text-gray-500">Narakeet voices, grouped by gender. Coach offers US &amp; GB. Fon has no accent/ethnicity field — audition to judge character. Cognate voices are only used where a mnemonic cites that language.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {voiceRoles.map((meta) => {
                const role = meta.role;
                const voices = meta?.voices || [];
                const inList = voices.some((v) => v.id === voiceConfig[role]);
                return (
                  <div key={role} className="block">
                    <div className="text-xs text-gray-400 mb-1">
                      {meta.label || ROLE_LABELS[role] || role}
                      {meta && <span className="text-gray-600"> · {voices.length} voices</span>}
                      {meta?.error && <span className="text-amber-500"> · list unavailable</span>}
                    </div>
                    <select
                      value={voiceConfig[role] || ''}
                      onChange={(e) => { const v = voices.find((x) => x.id === e.target.value); if (v) pickVoice(role, v); }}
                      className="w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none"
                    >
                      <option value="">— choose a voice —</option>
                      {(['F', 'M', ''] as const).map((g) => {
                        const list = voices.filter((v) => (v.gender || '') === g);
                        if (!list.length) return null;
                        return (
                          <optgroup key={g || 'o'} label={g === 'F' ? 'Female' : g === 'M' ? 'Male' : 'Other'}>
                            {list.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.region}{v.gender ? ` · ${v.gender}` : ''}</option>)}
                          </optgroup>
                        );
                      })}
                      {voiceConfig[role] && !inList && <option value={voiceConfig[role]}>{voiceConfig[role]} (manual)</option>}
                    </select>
                    <input
                      value={voiceConfig[role] || ''}
                      onChange={(e) => setVoiceManual(role, e.target.value, meta?.codes?.[0] || '')}
                      placeholder="or type a Fon voice id"
                      className="mt-1 w-full rounded bg-zinc-900/40 border border-zinc-800 px-2 py-1 text-xs text-gray-400 focus:border-accent-teal focus:outline-none"
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="text-xs text-gray-400">USD per audio-minute (Narakeet rate — for cost)</span>
                <input type="number" step="0.001" min="0" value={usdPerAudioMin} onChange={(e) => setUsdPerAudioMin(Number(e.target.value))}
                  className="mt-1 w-full sm:w-56 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none" />
              </label>
              <button onClick={onSaveVoices} disabled={saving}
                className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save voices'}
              </button>
            </div>
          </section>

          {/* 1b · Course info (MP3 tags) */}
          <section className="space-y-4">
            <h2 className="font-heading text-lg font-semibold text-gray-200">Course info (MP3 tags)</h2>
            <p className="text-xs text-gray-500">Shown on car and phone displays. Level, pattern and day are added automatically — e.g. Title “{(courseMeta.courseName || lang)} A1 · Pattern 1 · Day 1”, Album “{(courseMeta.courseName || lang)} A1”.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-xs text-gray-400">Course name (Album / Title)</span>
                <input value={courseMeta.courseName} onChange={(e) => setCourseMeta((m) => ({ ...m, courseName: e.target.value }))}
                  placeholder="e.g. Mandarin Taiwan Travel"
                  className="mt-1 w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Artist</span>
                <input value={courseMeta.artist} onChange={(e) => setCourseMeta((m) => ({ ...m, artist: e.target.value }))}
                  className="mt-1 w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Genre</span>
                <input value={courseMeta.genre} onChange={(e) => setCourseMeta((m) => ({ ...m, genre: e.target.value }))}
                  className="mt-1 w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none" />
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={onSaveMeta} disabled={savingMeta || retagging}
                className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">
                {savingMeta ? 'Saving…' : 'Save course info'}
              </button>
              <button onClick={onRetag} disabled={savingMeta || retagging}
                title="Apply this info to every already-built MP3 (no audio rebuild, no cost)"
                className="rounded bg-zinc-800 border border-zinc-700 text-gray-200 px-4 py-2 text-sm hover:bg-zinc-700 disabled:opacity-50">
                {retagging ? 'Re-tagging…' : 'Save + re-tag existing MP3s'}
              </button>
            </div>
          </section>

          {/* 2 · Scripts */}
          <section className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-heading text-lg font-semibold text-gray-200">2 · Scripts ({scripts.length})</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { const f = scripts.find((s) => s.pattern === 1 && s.day === 1)?.file; if (f) startBuild([f]); }} disabled={!!running}
                  className="rounded bg-zinc-800 border border-zinc-700 text-gray-200 px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-50"
                  title="Audition gate — build pattern 1, day 1 first (spec §11)">Build p01·d1 (audition)</button>
                <button onClick={() => startBuild([...selected])} disabled={!!running || selected.size === 0}
                  className="rounded bg-zinc-800 border border-zinc-700 text-gray-200 px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-50">Build selected ({selected.size})</button>
                <button onClick={() => setSelected(new Set())} disabled={selected.size === 0}
                  className="rounded bg-zinc-800 border border-zinc-700 text-gray-400 px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-50"
                  title="Clear the current selection">Unselect all</button>
                <button onClick={() => startBuild(notBuilt)} disabled={!!running || notBuilt.length === 0}
                  className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-3 py-1.5 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50"
                  title="Build every file that has no current MP3">Build all needed ({notBuilt.length})</button>
              </div>
            </div>
            <div className="overflow-x-auto rounded border border-zinc-800">
              <table className="min-w-[720px] w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-zinc-800">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Topic</th>
                    <th className="px-3 py-2 text-right">Speech</th>
                    <th className="px-3 py-2 text-right">Unique</th>
                    <th className="px-3 py-2 text-right">Chars</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {scripts.map((s) => {
                    const st = statusOf(s);
                    const base = s.file.replace(/\.json$/, '');
                    const o = outFor(s);
                    return (
                      <tr key={s.file} className="border-b border-zinc-900 hover:bg-white/5">
                        <td className="px-3 py-2"><input type="checkbox" checked={selected.has(s.file)} onChange={() => toggle(s.file)} /></td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-300">{base}{s.edited && <span className="ml-1 text-amber-400" title="edited from seed">✎</span>}</td>
                        <td className="px-3 py-2">
                          {st === 'current' && <span className="text-xs px-2 py-0.5 rounded bg-teal-900/30 text-teal-300">MP3 current</span>}
                          {st === 'stale' && <span className="text-xs px-2 py-0.5 rounded bg-amber-900/30 text-amber-300" title="Script edited since this MP3 was built">edited — rebuild</span>}
                          {st === 'none' && <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-gray-500">not built</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-400">{s.topic || <span className="text-gray-600">day {s.day}</span>}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{s.speech}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{s.unique}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{s.uniqueChars.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => openEditor(s.file)} className="text-xs text-gray-400 hover:text-accent-teal">Edit</button>
                          {o && <a href={o.mp3Url} className="ml-3 text-xs text-accent-teal hover:underline" download>MP3</a>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">Editing a line changes the file's version; its MP3 shows <span className="text-amber-300">edited — rebuild</span> until you rebuild. Unchanged lines are served from cache, so an edit only re-costs what actually changed.</p>
          </section>

          {/* 3 · Job */}
          {job && (
            <section className="space-y-4">
              <h2 className="font-heading text-lg font-semibold text-gray-200">3 · Build job</h2>
              <div className="rounded border border-zinc-800 p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${job.status === 'done' ? 'bg-teal-900/30 text-teal-300' : job.status === 'error' ? 'bg-red-900/30 text-red-300' : 'bg-amber-900/30 text-amber-300'}`}>{job.status}</span>
                  <span className="text-gray-400">{job.progress?.done ?? 0} / {job.progress?.total ?? job.files.length} files</span>
                  <span className="font-mono text-xs text-gray-600">{job.id}</span>
                </div>
                {job.error && <div className="text-red-300 text-sm whitespace-pre-wrap">{job.error}</div>}
                {job.stats && <StatsGrid stats={job.stats} />}
                {job.results && job.results.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-[560px] w-full text-xs">
                      <thead><tr className="text-left text-gray-500 border-b border-zinc-800">
                        <th className="px-2 py-1">File</th><th className="px-2 py-1 text-right">Duration</th><th className="px-2 py-1 text-right">Size</th>
                        <th className="px-2 py-1 text-right">Unique</th><th className="px-2 py-1 text-right">Cache</th><th className="px-2 py-1 text-right">Build</th>
                      </tr></thead>
                      <tbody>
                        {job.results.map((r) => (
                          <tr key={r.file} className="border-b border-zinc-900">
                            <td className="px-2 py-1 font-mono text-gray-300">{r.file}</td>
                            <td className="px-2 py-1 text-right text-gray-400">{fmtDur(r.durationMs)}</td>
                            <td className="px-2 py-1 text-right text-gray-400">{fmtBytes(r.bytes)}</td>
                            <td className="px-2 py-1 text-right text-gray-400">{r.unique}</td>
                            <td className="px-2 py-1 text-right text-gray-400">{r.cacheHitPct}%</td>
                            <td className="px-2 py-1 text-right text-gray-400">{Math.round(r.buildMs / 1000)}s</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}
          </>)}
        </div>
      )}

      {tab === 'library' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <CourseSettings catalog={CATALOG} levels={LEVELS}
              current={libLang ? { code: libLang, level: libLevel } : undefined}
              submitLabel="Show files"
              busy={libLoading}
              onSubmit={(c, l) => { setLibLang(c); setLibLevel(l); }} />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Speed</span>
              <div className="flex rounded border border-zinc-700 overflow-hidden">
                {SPEEDS.map((s) => (
                  <button key={s} onClick={() => setSpeed(s)}
                    className={`px-3 py-1.5 text-sm ${speed === s ? 'bg-accent-teal/25 text-accent-teal font-medium' : 'text-gray-400 hover:bg-white/5'}`}>
                    {s.toFixed(2).replace(/0$/, '')}×
                  </button>
                ))}
              </div>
            </div>
          </div>

          {!libLang ? (
            <p className="text-sm text-gray-500">Pick a <b>language</b>, then its <b>course</b> and a <b>level</b>, and click <b>Show files</b>.</p>
          ) : libLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : libOutputs.length === 0 ? (
            <p className="text-sm text-gray-500">No built files for <b>{COURSE_LABEL[libLang] || libLang}</b> {libLevel} yet. Build some in the Build tab.</p>
          ) : (
            <>
              <h3 className="font-heading text-lg font-semibold text-gray-100">
                {COURSE_LABEL[libLang] || libLang} <span className="text-gray-500 font-normal">· {libLevel} · {libOutputs.length} file{libOutputs.length === 1 ? '' : 's'}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {libOutputs.slice().sort((a, b) => (a.pattern - b.pattern) || (a.day - b.day)).map((o) => (
                  <PlayerCard key={o.file} o={o} speed={speed} />
                ))}
              </div>
            </>
          )}
          <p className="text-xs text-gray-500">Playback speed is applied in your browser — the audio itself is rendered at natural rate, so pauses scale with speed (spec §5.2). {speed}× selected.</p>
        </div>
      )}

      {tab === 'store' && (
        <div className="space-y-3">
          <div className="flex gap-1 bg-zinc-800 rounded p-0.5 w-fit">
            <button onClick={() => setStoreMode('ldp')} className={`px-3 py-1 text-sm rounded ${storeMode === 'ldp' ? 'bg-zinc-700 text-teal-300' : 'text-zinc-400 hover:text-zinc-200'}`}>Blanks form <span className="opacity-60">(autosave)</span></button>
            <button onClick={() => setStoreMode('classic')} className={`px-3 py-1 text-sm rounded ${storeMode === 'classic' ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-400 hover:text-zinc-200'}`}>Classic <span className="opacity-60">(covers/samples)</span></button>
          </div>
          {storeMode === 'ldp' ? <LdpStorePanel langs={LANGS} levels={LEVELS} /> : <StorePanel langs={LANGS} levels={LEVELS} />}
        </div>
      )}

      {editing && <EditorModal detail={editing} onClose={() => setEditing(null)} onSaved={afterEdit} onError={setError} />}
    </div>
  );
}

// ---- Store copy tab --------------------------------------------------------
// The 17 LDP blocks as pre-populated fields per product (lang+level). Bold /
// highlight buttons insert Michael's markup (**bold**, ==highlight==). Save
// persists to the product; "Copy all" hands the whole package to Blitzscale.
function StorePanel({ langs, levels }: { langs: Array<{ code: string; name: string }>; levels: string[] }) {
  const productId = (lg: string, lv: string) => `audio:${STORE_REGION[lg] || lg}:${lv}`;
  const seedFor = (lg: string, lv: string): StoreCopy =>
    STORE_SEEDS[`${lg}:${lv}`] ?? emptyStore(productId(lg, lv));

  const [lang, setLang] = useState('CMN_TW');
  const [level, setLevel] = useState('A1');
  const [copy, setCopy] = useState<StoreCopy>(() => seedFor('CMN_TW', 'A1'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selLang, setSelLang] = useState(() => languageOfCourse(langs, 'CMN_TW'));   // the chosen language; `lang` (below) is the chosen course within it
  const [srcLang, setSrcLang] = useState('CMN_TW');
  const [srcLevel, setSrcLevel] = useState('A1');
  const [publishing, setPublishing] = useState(false);
  const [pub, setPub] = useState<{ listing_id?: string; store_url?: string; version?: number } | null>(null);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [sampleOutputs, setSampleOutputs] = useState<Output[]>([]);
  const [samplePick, setSamplePick] = useState('');
  const [sampleBusy, setSampleBusy] = useState(false);
  const [coverLine, setCoverLine] = useState('cefr');
  const [coverLevel, setCoverLevel] = useState(level);
  const [coverBusy, setCoverBusy] = useState(false);
  const refs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const activeKey = useRef<string | null>(null);

  const load = useCallback(async (lg: string, lv: string) => {
    setLoading(true); setErr(null); setMsg(null);
    try {
      const r = await getStore(lg, lv);
      // Backfill any fields added after an older record was saved.
      setCopy(r.store ? { ...emptyStore(productId(lg, lv)), ...r.store } : seedFor(lg, lv));
    } catch (e) { setErr(e instanceof Error ? e.message : 'Load failed'); setCopy(seedFor(lg, lv)); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(lang, level); }, [lang, level, load]);
  // Built patterns for this course/level → the sample dropdown.
  useEffect(() => {
    let live = true;
    listOutputs(lang, level).then((r) => { if (live) setSampleOutputs(r.outputs || []); }).catch(() => { if (live) setSampleOutputs([]); });
    setSamplePick('');
    return () => { live = false; };
  }, [lang, level]);

  // Generate a landing-page sample: 60s audio clip + the matching companion page, saved behind the scenes.
  const addSample = async () => {
    const o = sampleOutputs.find((x) => x.file === samplePick);
    if (!o) return;
    setSampleBusy(true); setErr(null); setMsg(null);
    try {
      const a = await makeSample(lang, level, o.file, 60);
      let pageUrl = '';
      try {
        const p = await makeSamplePage('companion', bookLangOf(lang), level, o.pattern);
        pageUrl = Object.values(p.sample_pages || {})[0] || '';
      } catch { /* no companion page for this course/level — audio still added */ }
      const addLine = (blob: string, url: string) =>
        Array.from(new Set([...blob.split('\n').map((s) => s.trim()).filter(Boolean), url])).join('\n');
      setCopy((c) => ({
        ...c,
        sample_audio: addLine(c.sample_audio, a.sample_url),
        sample_pages: pageUrl ? addLine(c.sample_pages, pageUrl) : c.sample_pages,
      }));
      setMsg(pageUrl ? `Added Pattern ${o.pattern} — 60-second audio + matching page.` : `Added Pattern ${o.pattern} audio (no companion page for this course yet).`);
      setSamplePick('');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Sample generation failed'); }
    finally { setSampleBusy(false); }
  };
  const removeSample = (field: 'sample_audio' | 'sample_pages', url: string) =>
    setCopy((c) => ({ ...c, [field]: c[field].split('\n').map((s) => s.trim()).filter(Boolean).filter((u) => u !== url).join('\n') }));

  // Render the chosen book's front COVER page (title + framed image + branding) and add it as a sample.
  const addCoverPage = async (target: 'cover' | 'sample') => {
    setCoverBusy(true); setErr(null); setMsg(null);
    try {
      // Use the current store photo as the illustration when the book has no saved cover
      // (don't feed an already-rendered cover back in).
      // Companion (product) cover uses the photo YOU chose as the illustration; CEFR covers
      // use the CEFR book's own saved cover, so don't feed them the product photo.
      const illus = coverLine === 'companion'
        ? (/\/samples\/cover_/.test(copy.cover_image_url || '') ? undefined : (copy.cover_image_url || undefined))
        : undefined;
      // Companion cover = the product image → title it with the store product title.
      const title = coverLine === 'companion' ? (copy.title || undefined) : undefined;
      const r = await makeCoverPage(coverLine, bookLangOf(lang), coverLevel, illus, title);
      if (!r.ok || !r.cover_url) throw new Error(r.error || `Couldn't render the ${coverLevel} cover — set a cover image in Books, or choose a photo first.`);
      const url = r.cover_url;
      if (target === 'cover') {
        setCopy((c) => ({ ...c, cover_image_url: url }));
        setMsg(`Set the ${coverLevel} rendered cover as the product cover — Save, then Publish to push it to the store.`);
      } else {
        setCopy((c) => ({ ...c, sample_pages: Array.from(new Set([...c.sample_pages.split('\n').map((s) => s.trim()).filter(Boolean), url])).join('\n') }));
        setMsg(`Added the ${coverLevel} cover as a sample — Save, then Publish to push it to the store.`);
      }
    } catch (e) { setErr(e instanceof Error ? e.message : 'Cover render failed'); }
    finally { setCoverBusy(false); }
  };

  const setBlock = (key: string, val: string) => setCopy((c) => ({ ...c, blocks: { ...c.blocks, [key]: val } }));
  const setVar = (key: keyof StoreCopy['vars'], val: string) => setCopy((c) => ({ ...c, vars: { ...c.vars, [key]: val } }));
  const setField = (key: keyof StoreCopy, val: string) => setCopy((c) => ({ ...c, [key]: val }));

  // Wrap the current selection in the focused block textarea with markup.
  const wrap = (before: string, after: string) => {
    const key = activeKey.current;
    const ta = key ? refs.current[key] : null;
    if (!key || !ta) { setErr('Click into a copy field first, then Bold / Highlight.'); return; }
    setErr(null);
    const s = ta.selectionStart, e = ta.selectionEnd;
    const v = ta.value;
    const sel = v.slice(s, e) || 'text';
    const next = v.slice(0, s) + before + sel + after + v.slice(e);
    setBlock(key, next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = s + before.length;
      ta.selectionEnd = s + before.length + sel.length;
    });
  };

  const onSave = async () => {
    setSaving(true); setErr(null); setMsg(null);
    try { await saveStore(lang, level, { ...copy, product_id: copy.product_id || productId(lang, level) }); setMsg('Saved for ' + productId(lang, level) + '.'); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  };

  const onCopyFrom = async () => {
    setErr(null); setMsg(null);
    try {
      const r = await getStore(srcLang, srcLevel);
      const src = r.store ?? seedFor(srcLang, srcLevel);
      // Bring over the writing; keep THIS product's identity (title/id/author stay).
      setCopy((c) => ({
        ...c,
        vars: { ...src.vars },
        blocks: { ...src.blocks },
        bonuses: src.bonuses, bonus_covers: src.bonus_covers, upsell: src.upsell, fund_line: src.fund_line,
        success_instructions: src.success_instructions, success_upsell: src.success_upsell,
      }));
      setMsg(`Pre-populated from ${productId(srcLang, srcLevel)}. Review, then Save.`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Copy-from failed'); }
  };

  const onCopyAll = async () => {
    setErr(null); setMsg(null);
    const md = assembleStoreMarkdown({ ...copy, product_id: copy.product_id || productId(lang, level) });
    try { await navigator.clipboard.writeText(md); setMsg('Full copy package copied to clipboard — paste it to Blitzscale.'); }
    catch { setErr('Clipboard blocked. Select the preview text below and copy manually.'); }
  };

  // Publish/Update is idempotent (store upserts by product_id). It POSTs the serialized
  // payload live to the store, so confirm first.
  const onPublish = async () => {
    setErr(null); setMsg(null);
    const region = STORE_REGION[lang] || lang;
    const pid = copy.product_id || productId(lang, level);
    if (!window.confirm(`Publish ${pid} to the live store?\n\nThis sends the copy, samples, bonuses and suggested prices to Blitzscale. The store owns final pricing.`)) return;
    setPublishing(true);
    try {
      const payload = serializeStorePayload({ ...copy, product_id: pid }, { line: 'audio', language: region, level });
      const r = await publishStore(lang, level, payload);
      if (r.ok) { setPub({ listing_id: r.listing_id, store_url: r.store_url, version: r.version }); setMsg(`Published ${pid} (v${r.version}).`); }
      else setErr(`Publish failed${r.status ? ` (HTTP ${r.status})` : ''}: ${r.detail || 'unknown error'}`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Publish failed'); }
    finally { setPublishing(false); }
  };

  const onUnpublish = async () => {
    setErr(null); setMsg(null);
    const pid = copy.product_id || productId(lang, level);
    if (!window.confirm(`Unpublish ${pid}?\n\nThe listing is removed from the store. Existing buyers keep access.`)) return;
    setPublishing(true);
    try {
      const r = await unpublishStore(lang, level, pid);
      if (r.ok) { setPub(null); setMsg(`Unpublished ${pid}.`); }
      else setErr(`Unpublish failed${r.status ? ` (HTTP ${r.status})` : ''}: ${r.detail || 'unknown error'}`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Unpublish failed'); }
    finally { setPublishing(false); }
  };

  // Picked a cover from the image selector → upload the cropped blob → set cover_image_url.
  const onCoverPicked = async (blob: Blob) => {
    setCoverPickerOpen(false);
    setCoverUploading(true); setErr(null); setMsg(null);
    try {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      let bin = ''; for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
      const region = STORE_REGION[lang] || lang;
      const { url } = await uploadStoreCover(region, level, btoa(bin));
      setField('cover_image_url', url);
      setMsg('Cover image set — Save to keep it.');
    } catch (e) { setErr(e instanceof Error ? e.message : 'Cover upload failed'); }
    finally { setCoverUploading(false); }
  };

  const fieldCls = 'w-full rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none';
  const preview = assembleStoreMarkdown({ ...copy, product_id: copy.product_id || productId(lang, level) });

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Landing-page copy per product. Fields are pre-populated. <b>Bold</b> / <b>Highlight</b> insert the store markup
        (<code className="text-gray-400">**bold**</code>, <code className="text-gray-400">==highlight==</code>; branded terms are both,{' '}
        <code className="text-gray-400">**==term==**</code>). Save to keep it; <b>Copy all</b> puts the whole package on your clipboard to paste into Blitzscale.
      </p>

      {err && <div className="rounded border border-red-800/50 bg-red-900/20 text-red-300 px-4 py-3 text-sm whitespace-pre-wrap">{err}</div>}
      {msg && <div className="rounded border border-teal-800/50 bg-teal-900/20 text-teal-300 px-4 py-3 text-sm">{msg}</div>}

      {/* Product + toolbar */}
      <section className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs text-gray-400">Language</span>
          <select value={selLang} onChange={(e) => { const L = e.target.value; setSelLang(L); const cs = coursesOf(langs, L); if (cs[0]) setLang(cs[0].code); }} className={`mt-1 w-full sm:w-48 ${fieldCls}`}>
            {languagesOf(langs).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-400">Course</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className={`mt-1 w-full sm:w-44 ${fieldCls}`}>
            {coursesOf(langs, selLang).map((c) => <option key={c.code} value={c.code}>{editionOf(c.name)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-400">Level</span>
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={`mt-1 w-full sm:w-28 ${fieldCls}`}>
            {levels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <span className="text-xs text-gray-500 pb-2 font-mono">{productId(lang, level)}</span>
        <div className="flex flex-wrap gap-2 ml-auto">
          <button onClick={onSave} disabled={saving || loading}
            className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onCopyAll} disabled={loading}
            className="rounded bg-zinc-800 border border-zinc-700 text-gray-200 px-4 py-2 text-sm hover:bg-zinc-700 disabled:opacity-50">
            Copy all
          </button>
          <button onClick={onPublish} disabled={publishing || loading}
            title="Publish / update this listing on the live store (idempotent)"
            className="rounded bg-emerald-800/30 border border-emerald-600/40 text-emerald-300 px-4 py-2 text-sm font-medium hover:bg-emerald-800/50 disabled:opacity-50">
            {publishing ? 'Publishing…' : 'Publish / Update'}
          </button>
          <button onClick={onUnpublish} disabled={publishing || loading}
            className="rounded bg-zinc-800 border border-zinc-700 text-gray-400 px-3 py-2 text-sm hover:text-red-300 hover:bg-zinc-700 disabled:opacity-50">
            Unpublish
          </button>
        </div>
      </section>

      {pub && (
        <div className="rounded border border-emerald-800/50 bg-emerald-900/15 text-emerald-200 px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>Live on the store · <b>v{pub.version}</b></span>
          {pub.listing_id && <span className="font-mono text-xs text-emerald-300/80">{pub.listing_id}</span>}
          {pub.store_url && <a href={pub.store_url} target="_blank" rel="noreferrer" className="underline hover:text-emerald-100">Open store page →</a>}
        </div>
      )}

      {/* Pre-populate from another product */}
      <section className="flex flex-wrap items-end gap-2 rounded border border-zinc-800 p-3">
        <span className="text-xs text-gray-400 pb-2">Pre-populate from</span>
        <select value={srcLang} onChange={(e) => setSrcLang(e.target.value)} className={`w-full sm:w-44 ${fieldCls}`}>
          {langs.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <select value={srcLevel} onChange={(e) => setSrcLevel(e.target.value)} className={`w-full sm:w-24 ${fieldCls}`}>
          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <button onClick={onCopyFrom} disabled={loading}
          className="rounded bg-zinc-800 border border-zinc-700 text-gray-200 px-3 py-2 text-sm hover:bg-zinc-700 disabled:opacity-50">
          Copy its writing into this product
        </button>
        <span className="text-xs text-gray-600 pb-2">Keeps this product's title &amp; id; brings over blocks, prices, bonuses.</span>
      </section>

      {loading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <>
          {/* Header + template vars */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-gray-400">Title</span>
              <input value={copy.title} onChange={(e) => setField('title', e.target.value)} className={`mt-1 ${fieldCls}`} /></label>
            <label className="block"><span className="text-xs text-gray-400">Author</span>
              <input value={copy.author} onChange={(e) => setField('author', e.target.value)} className={`mt-1 ${fieldCls}`} /></label>
            <div className="block md:col-span-2">
              <span className="text-xs text-gray-400">Product cover image <span className="text-gray-600">(store card)</span></span>
              <div className="mt-1 flex items-start gap-3">
                {copy.cover_image_url
                  ? <img src={copy.cover_image_url} alt="cover" className="w-20 h-28 object-cover rounded border border-zinc-700" />
                  : <div className="w-20 h-28 rounded border border-dashed border-zinc-700 flex items-center justify-center text-[10px] text-gray-600 text-center px-1">No cover</div>}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-end gap-2">
                    <select value={coverLine} onChange={(e) => setCoverLine(e.target.value)}
                      className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-sm text-gray-100">
                      <option value="cefr">CEFR level book</option>
                      <option value="companion">Companion (Q&amp;A) book</option>
                    </select>
                    <select value={coverLevel} onChange={(e) => setCoverLevel(e.target.value)}
                      className="rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-sm text-gray-100">
                      {['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <button type="button" onClick={() => addCoverPage('cover')} disabled={coverBusy}
                      className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-3 py-1.5 text-sm hover:bg-accent-teal/30 disabled:opacity-50">
                      {coverBusy ? 'Rendering…' : 'Render book cover'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500">The store card should be the <b>rendered book cover</b> (title + branding) — not a bare photo. Use a custom image only for a bespoke design.</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-[11px] text-gray-600">Custom image:</span>
                    <button onClick={() => setCoverPickerOpen(true)} disabled={coverUploading}
                      className="rounded bg-zinc-800 border border-zinc-700 text-gray-300 px-3 py-1.5 text-xs hover:bg-zinc-700 disabled:opacity-50">
                      {coverUploading ? 'Uploading…' : 'Choose photo'}
                    </button>
                    {copy.cover_image_url && (
                      <button onClick={() => setField('cover_image_url', '')}
                        className="rounded bg-zinc-800 border border-zinc-700 text-gray-400 px-3 py-1.5 text-xs hover:text-red-300">Remove</button>
                    )}
                  </div>
                  <input value={copy.cover_image_url} onChange={(e) => setField('cover_image_url', e.target.value)} placeholder="…or paste an image URL"
                    className={`${fieldCls} font-mono text-xs`} />
                </div>
              </div>
            </div>
          </section>
          <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {([
              ['anchor_high', 'Anchor high'], ['anchor_low', 'Anchor low'], ['your_price', 'Your price'],
              ['bundle_price', 'Bundle price'], ['countdown', 'Countdown'], ['donation_amount', 'Donation amount'],
            ] as Array<[keyof StoreCopy['vars'], string]>).map(([k, lbl]) => (
              <label key={k} className="block"><span className="text-xs text-gray-400">{lbl} <code className="text-gray-600">{`{{${k}}}`}</code></span>
                <input value={copy.vars[k]} onChange={(e) => setVar(k, e.target.value)} className={`mt-1 ${fieldCls}`} /></label>
            ))}
          </section>

          {/* Markup toolbar (sticky) */}
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-zinc-950/90 backdrop-blur py-2 border-b border-zinc-800">
            <span className="text-xs text-gray-500">Selection:</span>
            <button onClick={() => wrap('**', '**')} className="rounded border border-zinc-700 px-3 py-1 text-sm font-bold text-gray-200 hover:bg-white/5">B</button>
            <button onClick={() => wrap('==', '==')} className="rounded border border-zinc-700 px-3 py-1 text-sm text-gray-200 hover:bg-white/5"><span className="bg-accent-teal/30 px-1 rounded">Highlight</span></button>
            <button onClick={() => wrap('**==', '==**')} className="rounded border border-zinc-700 px-3 py-1 text-sm font-bold text-gray-200 hover:bg-white/5"><span className="bg-accent-teal/30 px-1 rounded">Both</span></button>
            <span className="text-xs text-gray-600">Click into a field, select text, then a button.</span>
          </div>

          {/* 17 blocks */}
          <section className="space-y-4">
            {STORE_BLOCKS.map((b) => (
              <label key={b.key} className="block">
                <span className="text-xs text-gray-300 font-medium">{b.label}</span>
                {b.hint && <span className="text-xs text-amber-500/80 ml-2">{b.hint}</span>}
                <textarea
                  ref={(el) => { refs.current[b.key] = el; }}
                  value={copy.blocks[b.key] || ''}
                  onFocus={() => { activeKey.current = b.key; }}
                  onChange={(e) => setBlock(b.key, e.target.value)}
                  rows={Math.min(14, Math.max(2, (copy.blocks[b.key] || '').split('\n').length + 1))}
                  className={`mt-1 ${fieldCls} font-mono leading-relaxed`}
                />
              </label>
            ))}
          </section>

          {/* Bonuses / upsell / fund / samples */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block"><span className="text-xs text-gray-300 font-medium">Bonuses (one per line)</span>
              <textarea value={copy.bonuses} onChange={(e) => setField('bonuses', e.target.value)} rows={5} className={`mt-1 ${fieldCls} font-mono`} /></label>
            <label className="block"><span className="text-xs text-gray-300 font-medium">Bonus cover URLs <span className="text-gray-600">(one per line, same order as bonuses)</span></span>
              <textarea value={copy.bonus_covers} onChange={(e) => setField('bonus_covers', e.target.value)} rows={5} className={`mt-1 ${fieldCls} font-mono text-xs`} /></label>
            <div className="space-y-4">
              <label className="block"><span className="text-xs text-gray-300 font-medium">Order-bump / upsell</span>
                <textarea value={copy.upsell} onChange={(e) => setField('upsell', e.target.value)} rows={2} className={`mt-1 ${fieldCls} font-mono`} /></label>
              <label className="block"><span className="text-xs text-gray-300 font-medium">Fund line (bottom of page)</span>
                <textarea value={copy.fund_line} onChange={(e) => setField('fund_line', e.target.value)} rows={2} className={`mt-1 ${fieldCls} font-mono`} /></label>
            </div>
            <div className="block md:col-span-2 space-y-3 rounded border border-zinc-800 p-3">
              <span className="text-xs text-gray-300 font-medium">Samples — pick a pattern; we build a 60-second clip and the matching book page</span>
              <div className="flex flex-wrap items-end gap-2">
                <select value={samplePick} onChange={(e) => setSamplePick(e.target.value)}
                  className="block w-full sm:w-72 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100">
                  <option value="">Choose a pattern…</option>
                  {sampleOutputs.filter((o) => o.day === 1).map((o) => (
                    <option key={o.file} value={o.file}>Pattern {o.pattern} · Day {o.day}</option>
                  ))}
                </select>
                <button type="button" onClick={addSample} disabled={!samplePick || sampleBusy}
                  className="rounded bg-teal-800 px-4 py-2 text-sm font-medium text-teal-100 hover:bg-teal-700 disabled:opacity-50">
                  {sampleBusy ? 'Generating…' : 'Add sample'}
                </button>
                {sampleOutputs.length === 0 && <span className="text-xs text-gray-500">No built audio for this course &amp; level yet.</span>}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="block">
                  <span className="text-xs text-gray-400">Cover from book</span>
                  <select value={coverLine} onChange={(e) => setCoverLine(e.target.value)}
                    className="mt-1 block w-full sm:w-56 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100">
                    <option value="cefr">CEFR level book</option>
                    <option value="companion">Companion (Q&amp;A) book</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-gray-400">Level</span>
                  <select value={coverLevel} onChange={(e) => setCoverLevel(e.target.value)}
                    className="mt-1 block w-full sm:w-28 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100">
                    {['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </label>
                <button type="button" onClick={() => addCoverPage('sample')} disabled={coverBusy}
                  className="rounded bg-zinc-700 px-4 py-2 text-sm font-medium text-gray-100 hover:bg-zinc-600 disabled:opacity-50">
                  {coverBusy ? 'Rendering…' : 'Add cover page as sample'}
                </button>
                <span className="text-xs text-gray-500 w-full">Adds a book's front cover to the preview strip. (To set the <b>store card</b> cover, use “Render book cover” in the Product cover section above.)</span>
              </div>
              {(copy.sample_audio.trim() || copy.sample_pages.trim()) && (
                <div className="grid grid-cols-1 gap-2">
                  {copy.sample_audio.split('\n').map((s) => s.trim()).filter(Boolean).map((url) => (
                    <div key={url} className="flex items-center gap-2 rounded bg-zinc-900 border border-zinc-800 px-2 py-1.5">
                      <span className="text-[11px] font-mono text-teal-300 w-32 shrink-0">{patLabel(url)}</span>
                      <audio controls preload="none" src={url} className="h-8 flex-1 min-w-0" />
                      <button type="button" onClick={() => removeSample('sample_audio', url)}
                        className="shrink-0 rounded px-2.5 py-1 text-xs font-medium text-gray-200 bg-zinc-700 hover:bg-red-700 hover:text-white">Remove</button>
                    </div>
                  ))}
                  {copy.sample_pages.split('\n').map((s) => s.trim()).filter(Boolean).map((url) => (
                    <div key={url} className="flex items-center gap-2 rounded bg-zinc-900 border border-zinc-800 px-2 py-1.5">
                      <img src={url} alt="sample page" className="h-14 w-auto rounded border border-zinc-700" loading="lazy" />
                      <span className="text-[11px] font-mono text-gray-500 flex-1 min-w-0 truncate">{url.split('/').pop()}</span>
                      <button type="button" onClick={() => removeSample('sample_pages', url)}
                        className="shrink-0 rounded px-2.5 py-1 text-xs font-medium text-gray-200 bg-zinc-700 hover:bg-red-700 hover:text-white">Remove</button>
                    </div>
                  ))}
                  <p className="text-[11px] text-gray-500">Add/Remove edits this lineup — click <b>Save</b> to keep it, then <b>Publish / Update</b> to push it to the live store &amp; landing page. Save alone does not update the store.</p>
                </div>
              )}
            </div>
          </section>

          {/* Post-purchase success page */}
          <section className="space-y-4 rounded border border-zinc-800 p-3">
            <h3 className="font-heading text-sm font-semibold text-gray-300">Post-purchase success page</h3>
            <p className="text-xs text-gray-500">Shown after checkout. Usage instructions + the Pro/Plus subscription upsell. Flows to Blitzscale with "Copy all".</p>
            <label className="block"><span className="text-xs text-gray-300 font-medium">How to use the product</span>
              <textarea value={copy.success_instructions} onChange={(e) => setField('success_instructions', e.target.value)} rows={9} className={`mt-1 ${fieldCls} font-mono leading-relaxed`} /></label>
            <label className="block"><span className="text-xs text-gray-300 font-medium">Pro/Plus subscription upsell</span>
              <textarea value={copy.success_upsell} onChange={(e) => setField('success_upsell', e.target.value)} rows={4} className={`mt-1 ${fieldCls} font-mono leading-relaxed`} /></label>
          </section>

          {/* Assembled preview (also the manual-copy fallback) */}
          <section className="space-y-2">
            <h3 className="font-heading text-sm font-semibold text-gray-300">Assembled package (what "Copy all" copies)</h3>
            <textarea readOnly value={preview} rows={16}
              className={`${fieldCls} font-mono text-xs text-gray-400`} onFocus={(e) => e.currentTarget.select()} />
          </section>
        </>
      )}

      <ImagePicker
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        onSelect={(blob) => onCoverPicked(blob)}
        slot={'cover_female' as ImagePickerSlot}
        languageCode={bookLangOf(lang)}
      />
    </div>
  );
}

const SKIPS: Array<[string, number]> = [
  ['−5m', -300], ['−1m', -60], ['−10s', -10], ['+10s', 10], ['+1m', 60], ['+5m', 300],
];

function PlayerCard({ o, speed }: { o: Output; speed: number }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => { if (ref.current) ref.current.playbackRate = speed; }, [speed]);
  const skip = (d: number) => {
    const a = ref.current;
    if (!a) return;
    const end = isFinite(a.duration) ? a.duration : Infinity;
    a.currentTime = Math.max(0, Math.min(end, a.currentTime + d));
  };
  return (
    <div className="rounded border border-zinc-800 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-gray-300">{o.file}</span>
        <span className="text-xs text-gray-500">{fmtDur(o.durationMs)} · {fmtBytes(o.bytes)}</span>
      </div>
      <audio ref={ref} controls preload="none" src={o.mp3Url} className="w-full"
        onPlay={() => {
          // Car/phone "now playing" otherwise shows the page title (Glossika) + origin.
          // Set a proper track title so it reads "Glossika <language> <course> <level>".
          try {
            if ("mediaSession" in navigator) {
              const m = o.file.match(/^(.*)_([ABC][12])_p\d+_d\d+$/);
              const code = (m && m[1]) || o.file;
              const lvl = (m && m[2]) || "";
              const pretty = code.split("_")
                .map((s) => (s.length <= 2 ? s.toUpperCase() : s.charAt(0).toUpperCase() + s.slice(1)))
                .join(" ");
              navigator.mediaSession.metadata = new MediaMetadata({
                title: `Glossika ${pretty} ${lvl}`.replace(/\s+/g, " ").trim(),
                artist: "Glossika",
                album: "Glossika Audio Course",
              });
            }
          } catch { /* mediaSession unsupported */ }
        }}
        onLoadedMetadata={(e) => { e.currentTarget.playbackRate = speed; }} />
      <div className="flex flex-wrap justify-center gap-1">
        {SKIPS.map(([label, d]) => (
          <button key={label} onClick={() => skip(d)}
            className="px-2 py-1 text-xs rounded border border-zinc-700 text-gray-300 hover:bg-white/5 hover:text-accent-teal font-mono">
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <a href={o.mp3Url} download className="text-accent-teal hover:underline">Download MP3</a>
        <a href={o.txtUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:underline">Transcript</a>
      </div>
    </div>
  );
}

function StatsGrid({ stats }: { stats: JobStats }) {
  const tiles: Array<[string, string]> = [
    ['Files built', String(stats.filesBuilt)],
    ['Speech segments', `${stats.uniqueSpeechSegments} / ${stats.totalSpeechSegments}`],
    ['Fon TTS calls', stats.ttsCalls.toLocaleString()],
    ['Cache hit', `${stats.cacheHitPct}%`],
    ['Characters → Fon', stats.charactersToFon.toLocaleString()],
    ['Audio rendered', `${stats.audioMinutes} min`],
    ['Billed (new) audio', `${stats.billedAudioMinutes} min`],
    ['Assembly time', `${stats.buildWallSeconds}s`],
    ['Est. cost', stats.estimatedCostUsd != null ? `$${stats.estimatedCostUsd.toFixed(2)}` : '—'],
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {tiles.map(([k, v]) => (
          <div key={k} className="rounded bg-zinc-900/60 border border-zinc-800 px-3 py-2">
            <div className="text-[11px] text-gray-500">{k}</div>
            <div className="font-mono text-sm text-gray-100">{v}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-500">
        Cost is charged only on newly-generated audio: {stats.billedAudioMinutes} min × ${stats.usdPerAudioMin}/min
        {stats.estimatedCostUsd != null && <> = <span className="text-gray-300">${stats.estimatedCostUsd.toFixed(2)}</span></>}.
        {' '}Cached lines (rebuilds, edits, repeats) are free; assembly is AWS compute (~$0.02).
      </div>
    </div>
  );
}

function EditorModal({ detail, onClose, onSaved, onError }: {
  detail: ScriptDetail; onClose: () => void; onSaved: () => void; onError: (m: string) => void;
}) {
  // Dedupe by (role,text): one input per unique line, applied to every occurrence.
  const uniq: Array<{ role: string; text: string; ids: string[] }> = [];
  const seen = new Map<string, number>();
  for (const l of detail.lines) {
    const key = `${l.role}|${l.text}`;
    const idx = seen.get(key);
    if (idx !== undefined) uniq[idx]!.ids.push(l.id);
    else { seen.set(key, uniq.length); uniq.push({ role: l.role, text: l.text, ids: [l.id] }); }
  }
  const [vals, setVals] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const lines: Record<string, string> = {};
      uniq.forEach((u, i) => { const nv = vals[i]; if (nv != null && nv !== u.text) u.ids.forEach((id) => { lines[id] = nv; }); });
      if (Object.keys(lines).length === 0) { onClose(); return; }
      await saveScript(detail.lang, detail.file, lines);
      onSaved();
    } catch (e) { onError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setBusy(false); }
  };
  const doReset = async () => {
    setBusy(true);
    try { await resetScript(detail.lang, detail.file); onSaved(); }
    catch (e) { onError(e instanceof Error ? e.message : 'Reset failed'); }
    finally { setBusy(false); }
  };

  const roleTag: Record<string, string> = { coach: 'COACH', model_q: 'ASK', model_a: 'ANSWER', cognate: 'COGNATE' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg border border-zinc-700 bg-zinc-950 p-4 md:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg text-gray-100">Edit {detail.file}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">✕</button>
        </div>
        <p className="text-xs text-gray-500 mb-3">{uniq.length} unique lines. Coach lines are English (no target text); target lines are the sentences spoken by the voices. Edits are cached by text — only changed lines re-cost.</p>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {uniq.map((u, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`mt-2 text-[10px] font-mono w-16 shrink-0 ${u.role === 'coach' ? 'text-gray-500' : 'text-accent-teal'}`}>{roleTag[u.role] || u.role}</span>
              <input value={vals[i] ?? u.text} onChange={(e) => setVals((v) => ({ ...v, [i]: e.target.value }))}
                dir="auto" className="flex-1 rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-sm text-gray-100 focus:border-accent-teal focus:outline-none" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <button onClick={doReset} disabled={busy || !detail.edited} className="text-xs text-gray-500 hover:text-red-300 disabled:opacity-40">Reset to original</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded border border-zinc-700 text-gray-300 px-4 py-2 text-sm hover:bg-white/5">Cancel</button>
            <button onClick={save} disabled={busy} className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">{busy ? 'Saving…' : 'Save edits'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
